import { useEffect, useState } from "react";
import { X, KeyRound, Lock } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PROFILE } from "@/constants/testIds";
import { useAuth } from "@/context/AuthContext";

export default function ChangePasswordModal({ open, onClose }) {
    const { user, setUser } = useAuth();
    const [current, setCurrent] = useState("");
    const [next, setNext] = useState("");
    const [confirm, setConfirm] = useState("");
    const [err, setErr] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) { setCurrent(""); setNext(""); setConfirm(""); setErr(""); setSaving(false); }
    }, [open]);

    if (!open) return null;

    const clientValid = current && next.length >= 8 && next === confirm && next !== current;
    const mismatch = confirm && next !== confirm;
    const tooShort = next && next.length < 8;
    const same = next && next === current;

    const submit = async () => {
        setErr("");
        if (!clientValid) return;
        setSaving(true);
        try {
            const { data } = await api.post("/auth/change-password", {
                current_password: current,
                new_password: next,
                confirm_password: confirm,
            });
            // Refresh token so the current tab keeps a fresh 12h session.
            if (data?.access_token) {
                localStorage.setItem("mss_token", data.access_token);
            }
            if (user) setUser({ ...user, password_is_seed: false });
            toast.success("Password changed");
            onClose?.();
        } catch (ex) {
            setErr(formatApiError(ex?.response?.data?.detail) || "Password change failed");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center" role="dialog" aria-modal="true">
            <div className="absolute inset-0 bg-black/50" onClick={() => !saving && onClose?.()} />
            <div
                data-testid={PROFILE.modal}
                className="relative bg-white border border-neutral-200 shadow-2xl w-full max-w-[440px] rounded-[3px]"
            >
                <div className="px-6 py-5 border-b border-neutral-200 flex items-start justify-between">
                    <div className="flex items-start gap-3">
                        <span
                            className="inline-flex items-center justify-center w-8 h-8 rounded-[2px]"
                            style={{ background: "var(--kp-red)" }}
                        >
                            <Lock size={16} className="text-white" />
                        </span>
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.24em] text-neutral-500 font-display">
                                Profile
                            </div>
                            <div className="font-display font-bold text-xl text-[color:var(--kp-charcoal)] mt-0.5">
                                Change password
                            </div>
                        </div>
                    </div>
                    <button
                        data-testid={PROFILE.modalClose}
                        onClick={() => !saving && onClose?.()}
                        className="p-1 text-neutral-400 hover:text-black"
                        aria-label="Cancel"
                    >
                        <X size={16} />
                    </button>
                </div>

                <div className="px-6 py-5 space-y-4">
                    <div>
                        <label htmlFor={PROFILE.inputCurrent} className="block text-[11px] uppercase tracking-[0.14em] text-neutral-500 font-display mb-1">
                            Current password
                        </label>
                        <input
                            id={PROFILE.inputCurrent}
                            data-testid={PROFILE.inputCurrent}
                            type="password"
                            autoFocus
                            autoComplete="current-password"
                            value={current}
                            onChange={(e) => setCurrent(e.target.value)}
                            className="kp-focus w-full border border-neutral-300 px-3 py-2 text-sm bg-white rounded-[3px]"
                        />
                    </div>
                    <div>
                        <label htmlFor={PROFILE.inputNew} className="block text-[11px] uppercase tracking-[0.14em] text-neutral-500 font-display mb-1">
                            New password (min 8 characters)
                        </label>
                        <input
                            id={PROFILE.inputNew}
                            data-testid={PROFILE.inputNew}
                            type="password"
                            autoComplete="new-password"
                            value={next}
                            onChange={(e) => setNext(e.target.value)}
                            className="kp-focus w-full border border-neutral-300 px-3 py-2 text-sm bg-white rounded-[3px]"
                        />
                    </div>
                    <div>
                        <label htmlFor={PROFILE.inputConfirm} className="block text-[11px] uppercase tracking-[0.14em] text-neutral-500 font-display mb-1">
                            Confirm new password
                        </label>
                        <input
                            id={PROFILE.inputConfirm}
                            data-testid={PROFILE.inputConfirm}
                            type="password"
                            autoComplete="new-password"
                            value={confirm}
                            onChange={(e) => setConfirm(e.target.value)}
                            className="kp-focus w-full border border-neutral-300 px-3 py-2 text-sm bg-white rounded-[3px]"
                        />
                        {tooShort && (
                            <div className="mt-1 text-[11px] text-neutral-500">Use at least 8 characters.</div>
                        )}
                        {mismatch && !tooShort && (
                            <div className="mt-1 text-[11px]" style={{ color: "var(--kp-red)" }}>Passwords don&rsquo;t match.</div>
                        )}
                        {same && !mismatch && !tooShort && (
                            <div className="mt-1 text-[11px]" style={{ color: "var(--kp-red)" }}>New password must be different.</div>
                        )}
                    </div>

                    {err && (
                        <div
                            data-testid={PROFILE.error}
                            className="text-[12px] px-3 py-2 border-l-2 rounded-[2px]"
                            style={{ borderLeftColor: "var(--kp-red)", background: "#FFF5F5", color: "#8A0713" }}
                        >
                            {err}
                        </div>
                    )}
                </div>

                <div className="px-6 py-4 border-t border-neutral-200 flex justify-end gap-3">
                    <button
                        onClick={() => !saving && onClose?.()}
                        className="px-4 py-2 text-[12px] font-display font-semibold uppercase tracking-[0.12em] text-neutral-600 hover:text-black"
                    >
                        Cancel
                    </button>
                    <button
                        data-testid={PROFILE.submitBtn}
                        onClick={submit}
                        disabled={!clientValid || saving}
                        className="inline-flex items-center gap-2 px-5 py-2 text-[12px] font-display font-semibold uppercase tracking-[0.14em] text-white rounded-[3px] disabled:opacity-40 disabled:cursor-not-allowed"
                        style={{ background: "var(--kp-red)" }}
                    >
                        <KeyRound size={14} /> {saving ? "Changing…" : "Change password"}
                    </button>
                </div>
            </div>
        </div>
    );
}
